import React, { useMemo, useState } from "react";
import { Check, Play } from "lucide-react";
import { Button } from "../ui/button";
import ChecklistSheet from "../workout/FreeTrainingChecklist";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { useToast } from "../../contexts/ToastContext";
import {
  buildExerciseList,
  checklistEmpty,
  checklistSessions,
} from "../../utils/freeTrainingLog";
import { countsAsForDayKind, writtenSessionPlan } from "../../utils/writtenSession";
import { slotName } from "../../utils/programmeSchedule";
import { slotPlan } from "../../config/periodisation";
import CountsAsPicker from "../profile/CountsAsPicker";

/**
 * Write a programme session yourself (#173): type the exercises the way Free
 * training's checklist does, or start from last time's or a saved session's,
 * then save it to the slot (or save and start). Mounted only while open, so
 * each opening starts from nothing.
 */
const WriteSessionSheet = ({ programme, session, onSave, onStart, onClose }) => {
  const { sessions, performanceCapabilities } = useUserProfile();
  const { showToast } = useToast();
  const list = useMemo(
    () => buildExerciseList(sessions, performanceCapabilities),
    [sessions, performanceCapabilities],
  );
  const from = useMemo(() => checklistSessions(sessions), [sessions]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  // What it counts toward (#173), from the slot's day kind until you change it.
  const [countsAs, setCountsAs] = useState(() =>
    countsAsForDayKind(programme ? slotPlan(programme, session).kind : session?.day_kind),
  );

  const name = slotName(session);
  const empty = checklistEmpty(plan);

  const finish = async (then) => {
    const written = writtenSessionPlan(plan, name, countsAs);
    if (!written) return;
    setBusy(true);
    try {
      await then(written);
    } catch {
      setBusy(false);
      showToast({
        title: "Couldn't save this session",
        description: "Check your connection, then try again.",
      });
    }
  };

  const footer = (
    <div className="grid grid-cols-2 gap-2">
      <Button
        type="button"
        variant="outline"
        className="h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
        disabled={empty || busy}
        onClick={() => finish(onSave)}
      >
        <Check className="mr-1.5 h-4 w-4" />
        Save
      </Button>
      <Button
        type="button"
        className="h-12 w-full text-base font-bold"
        disabled={empty || busy}
        onClick={() => finish(onStart)}
      >
        <Play className="mr-1.5 h-4 w-4 fill-current" />
        Start
      </Button>
    </div>
  );

  return (
    <ChecklistSheet
      checklist={plan}
      onChange={setPlan}
      list={list}
      sessions={from}
      onClose={onClose}
      title={name}
      description={`Week ${session?.week_number} · Type what you'll do. Khronos adds a rest between sets.`}
      footer={footer}
      addLabel="Add an exercise"
      extra={
        !empty && (
          <div className="grid gap-2">
            <h2
              id="write-counts-as"
              className="px-0.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground"
            >
              Counts as
            </h2>
            <CountsAsPicker id="write-counts-as" value={countsAs} onChange={setCountsAs} />
          </div>
        )
      }
    />
  );
};

export default WriteSessionSheet;
